import axios from 'axios'
import tokenService from '../_services/tokenService'

// Create axios instance
const httpClient = axios.create({
    baseURL: process.env.REACT_APP_API_SERVER,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
    },
    withCredentials: true,
})

// Token initialization promise to ensure it's only done once
let tokenInitPromise = null;

// Function to ensure token is initialized
const ensureTokenInitialized = async () => {
    if (!tokenInitPromise) {
        tokenInitPromise = tokenService.initialize();
    }
    return tokenInitPromise;
};

// Request interceptor to add authentication token
httpClient.interceptors.request.use(
    async (config) => {
        try {
            // Get current token
            let token = tokenService.getToken();

            // If no token, try to initialize token service
            if (!token) {
                console.log('🔄 No token found, initializing token service...');
                token = await ensureTokenInitialized();
            }

            // Add token to request if available
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
                console.log('🔑 Token added to request:', config.url);
            } else {
                console.log('⚠️ No token available for request:', config.url);
                
                // For non-auth endpoints, continue without token
                const isAuthRequired = !config.url?.includes('/login') && 
                                     !config.url?.includes('/heartbeat') &&
                                     !config.url?.includes('/public');
                
                if (isAuthRequired) {
                    console.warn('🚨 Auth required but no token available for:', config.url);
                }
            }

            return config;
        } catch (error) {
            console.error('❌ Token initialization error:', error);
            // Continue with request even if token initialization fails
            return config;
        }
    },
    (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
)

// Response interceptor to handle token expiration
httpClient.interceptors.response.use(
    response => {
        // Successful response, return as-is
        return response
    },
    error => {
        const { response } = error

        if (response) {
            // Handle different HTTP status codes
            switch (response.status) {
                case 401:
                    console.warn(
                        '🚫 Unauthorized (401) - Token may be expired or invalid'
                    )

                    // Clear invalid token
                    tokenService.clearToken()

                    // Try to refresh token
                    tokenService.refreshToken().then(newToken => {
                        if (newToken) {
                            console.log('🔄 Token refreshed, retry the request')
                            // Optionally retry the original request
                            return httpClient.request(error.config)
                        } else {
                            console.log(
                                '❌ Token refresh failed, redirect to login'
                            )
                            // Redirect to login or show login modal
                            window.location.href = '/login'
                        }
                    })
                    break

                case 403:
                    console.warn(
                        '🚫 Forbidden (403) - Insufficient permissions'
                    )
                    break

                case 404:
                    console.warn('🔍 Not Found (404):', response.config?.url)
                    break

                case 500:
                    console.error('💥 Server Error (500)')
                    break

                default:
                    console.error(
                        `❌ HTTP Error ${response.status}:`,
                        response.statusText
                    )
            }
        } else {
            // Network error or request timeout
            console.error('🌐 Network Error:', error.message)
        }

        return Promise.reject(error)
    }
)

// Helper function to manually set token (for backward compatibility)
httpClient.setToken = token => {
    if (token) {
        tokenService.setToken(token, 'manual')
        console.log('🔑 Token manually set in HTTP client')
    } else {
        tokenService.clearToken()
        console.log('🗑️ Token cleared from HTTP client')
    }
}

// Helper function to get current token
httpClient.getToken = () => {
    return tokenService.getToken()
}

// Helper function to check if authenticated
httpClient.isAuthenticated = () => {
    const token = tokenService.getToken()
    return token && tokenService.isTokenValid()
}

// Helper function to make authenticated requests
httpClient.authenticatedRequest = async config => {
    // Ensure token is available
    if (!tokenService.getToken()) {
        await tokenService.initialize()
    }

    if (!tokenService.getToken()) {
        throw new Error('No authentication token available')
    }

    return httpClient.request(config)
}

// Helper functions for common HTTP methods with automatic token
httpClient.authGet = (url, config = {}) => {
    return httpClient.authenticatedRequest({ ...config, method: 'GET', url })
}

httpClient.authPost = (url, data, config = {}) => {
    return httpClient.authenticatedRequest({
        ...config,
        method: 'POST',
        url,
        data,
    })
}

httpClient.authPut = (url, data, config = {}) => {
    return httpClient.authenticatedRequest({
        ...config,
        method: 'PUT',
        url,
        data,
    })
}

httpClient.authDelete = (url, config = {}) => {
    return httpClient.authenticatedRequest({ ...config, method: 'DELETE', url })
}

export default httpClient
